<script lang="ts">
  import GridLines from '$lib/components/GridLines.svelte';
  import GridContent from '$lib/components/GridContent.svelte';
  import GridInner from '$lib/components/GridInner.svelte';
  import Star from '$lib/components/Star.svelte';

  // CRF walkthrough — matches the pattern on /product/security
  const crfSteps = [
    { step: '01', label: 'Exposure',   detail: 'An agent reads an email, webpage, or file from an untrusted source.' },
    { step: '02', label: 'Detection',  detail: 'Hidden injections are surfaced as visible text requiring human review.' },
    { step: '03', label: 'Constraint', detail: 'Subsequent actions are restricted — elevated approval, scoped tools, human-in-the-loop.' },
    { step: '04', label: 'Audit',      detail: 'Every decision logged with full context. Immutable. Queryable.' },
  ];

  // Compliance/sovereignty indicators — short form for landing.
  // Full sector treatment lives on /why-corral#sovereignty.
  const regulations = [
    { code: 'GDPR',     note: 'EU data protection' },
    { code: 'EU AI Act', note: 'EU AI governance' },
    { code: 'HIPAA',    note: 'US healthcare' },
    { code: 'FedRAMP',  note: 'US government' },
    { code: 'SOC 2',    note: 'Controls attestation' },
  ];
</script>

<section id="control" class="bg-[#0d0d0d] relative">
  <GridLines variant="dark" />

  <GridContent>
    <GridInner>
      <div class="py-[80px] lg:py-[110px]">
        <!-- Header: the fused thesis -->
        <div class="flex flex-col items-start lg:items-center text-left lg:text-center mb-16 lg:mb-20 max-w-[820px] mx-auto">
          <p class="font-mono text-[12px] uppercase tracking-[0.12em] text-[#ee6018] mb-5">
            04 · Control &amp; Sovereignty
          </p>
          <h2 class="font-display font-[300] text-[40px] md:text-[52px] lg:text-[60px] leading-[1.02] tracking-[-0.035em] text-white mb-6">
            Control what AI does. Own where it runs.
          </h2>
          <p class="font-body font-light text-[17px] lg:text-[19px] leading-[1.55] text-[#B8B8B8] max-w-[640px]">
            The architecture that lets you customize AI safely is the same architecture that meets your regulators. Security and sovereignty are two consequences of one design decision.
          </p>
        </div>

        <!-- PART A: Control what AI does -->
        <div class="mb-20 lg:mb-24">
          <div class="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)] gap-10 lg:gap-[80px] mb-12 lg:mb-14">
            <!-- Left: narrative -->
            <div class="max-w-[480px]">
              <p class="font-mono text-[11px] uppercase tracking-[0.14em] text-[#878787] mb-4">
                Part A · Control what AI does
              </p>
              <h3 class="font-display font-[300] text-[28px] lg:text-[34px] leading-[1.1] tracking-[-0.025em] text-white mb-6">
                Agents are a new type of user. And user behavior has always been the hard security problem.
              </h3>
              <p class="font-body font-light text-[16px] leading-[1.55] text-[#B8B8B8] mb-4">
                Agents can be manipulated through their inputs: prompt injections from emails, tainted reasoning from web content, chained tool calls that propagate untrusted data into trusted systems.
              </p>
              <p class="font-body font-light text-[16px] leading-[1.55] text-white">
                Corral's Cumulative Restrictions Framework tracks what an agent has been exposed to and constrains what it can do next. It's not a bolt-on filter — it's the architecture. That's what makes safe extensibility possible.
              </p>
            </div>

            <!-- Right: pull quote + diagram callout -->
            <div class="lg:pt-10">
              <div class="border-l-2 border-[#ee6018] pl-6 py-2 mb-8">
                <p class="font-display font-[300] text-[20px] lg:text-[24px] leading-[1.3] tracking-[-0.02em] text-white">
                  Hidden injections are surfaced as visible text requiring human review.
                </p>
              </div>
              <div class="border border-[#333335] bg-[#171719] p-5 font-mono text-[12px] text-[#878787] leading-relaxed">
                <p class="text-[#ee6018] mb-3">// agent exposed to untrusted content</p>
                <p>$ agent.read(<span class="text-white">"inbox/support-ticket-882"</span>)</p>
                <p>→ taint: <span class="text-[#ee6018]">tracked</span></p>
                <p>→ next_action: <span class="text-white">issue_refund()</span></p>
                <p>→ gate: <span class="text-[#ee6018]">human approval required</span></p>
                <p class="mt-3 text-[#5C5855]">// CRF applied, not a policy check</p>
              </div>
            </div>
          </div>

          <!-- CRF 4-step flow -->
          <div class="relative">
            <!-- Horizontal line spanning the grid edges -->
            <div class="hidden lg:block absolute top-0 -left-[calc(54px+9.03vw)] -right-[calc(54px+9.03vw)] h-px bg-[#333335] z-[5]" aria-hidden="true"></div>
            <Star variant="full" class="hidden lg:block absolute -left-[63px] top-[-9px]" />
            <Star variant="full" class="hidden lg:block absolute -right-[63px] top-[-9px]" />

            <div class="hidden lg:block absolute bottom-0 -left-[calc(54px+9.03vw)] -right-[calc(54px+9.03vw)] h-px bg-[#333335] z-[5]" aria-hidden="true"></div>
            <Star variant="full" class="hidden lg:block absolute -left-[63px] bottom-[-9px]" />
            <Star variant="full" class="hidden lg:block absolute -right-[63px] bottom-[-9px]" />

            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0">
              {#each crfSteps as s, i}
                <div class="bg-[#0d0d0d] p-6 lg:p-7 relative {i > 0 ? 'lg:border-l lg:border-l-[#333335]' : ''} {i >= 2 ? 'border-t md:border-t-0 md:[&:nth-child(3)]:border-t lg:[&:nth-child(3)]:border-t-0 border-[#333335]' : ''}">
                  <div class="flex items-baseline gap-3 mb-3">
                    <span class="font-mono text-[11px] text-[#ee6018]">{s.step}</span>
                    <span class="font-display text-[17px] text-white tracking-tight">{s.label}</span>
                  </div>
                  <p class="font-body font-light text-[13.5px] leading-[1.5] text-[#878787]">{s.detail}</p>
                  {#if i < crfSteps.length - 1}
                    <span class="hidden lg:block absolute top-1/2 -translate-y-1/2 -right-[7px] text-[#ee6018] font-mono text-[14px] bg-[#0d0d0d] w-[14px] h-[14px] flex items-center justify-center z-[6]" aria-hidden="true">→</span>
                  {/if}
                </div>
              {/each}
            </div>
          </div>
        </div>

        <!-- PART B: Own where it runs -->
        <div>
          <div class="grid grid-cols-1 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] gap-10 lg:gap-[80px]">
            <!-- Left: compliance band -->
            <div class="order-2 lg:order-1">
              <p class="font-mono text-[11px] uppercase tracking-[0.14em] text-[#878787] mb-4">
                Regulatory reality
              </p>
              <div class="flex flex-wrap gap-[1px] bg-[#333335] border border-[#333335]">
                {#each regulations as r}
                  <div class="bg-[#171719] px-5 py-4 flex-1 min-w-[140px]">
                    <p class="font-display text-[17px] text-white tracking-tight mb-1">{r.code}</p>
                    <p class="font-mono text-[11px] text-[#878787]">{r.note}</p>
                  </div>
                {/each}
              </div>
              <p class="font-body font-light text-[14px] leading-[1.55] text-[#878787] mt-6">
                Data residency requirements vary by region, contract, and jurisdiction. On-tenant deployment is the architecture that meets them by design — not by policy amendment.
              </p>
            </div>

            <!-- Right: narrative -->
            <div class="order-1 lg:order-2 max-w-[480px]">
              <p class="font-mono text-[11px] uppercase tracking-[0.14em] text-[#878787] mb-4">
                Part B · Own where it runs
              </p>
              <h3 class="font-display font-[300] text-[28px] lg:text-[34px] leading-[1.1] tracking-[-0.025em] text-white mb-6">
                Control isn't a preference. It's becoming law.
              </h3>
              <p class="font-body font-light text-[16px] leading-[1.55] text-[#B8B8B8] mb-4">
                The EU AI Act. GDPR. HIPAA. FedRAMP. Sector-specific rules in healthcare, finance, government, and education. Control over where your AI runs isn't a nice-to-have anymore — it's a compliance line item.
              </p>
              <p class="font-body font-light text-[16px] leading-[1.55] text-white">
                Your AI runs in your Azure tenant. Your data never leaves your jurisdiction. Corral can't access it — not because we pinky-promise, but because we architected it that way from the start.
              </p>
            </div>
          </div>

          <!-- Unifying closing line -->
          <div class="mt-14 lg:mt-16 pt-10 border-t border-[#333335]">
            <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
              <p class="font-display font-[300] text-[22px] lg:text-[28px] leading-[1.25] tracking-[-0.02em] text-white max-w-[720px]">
                Your data never leaves your tenant. <span class="text-[#878787]">Not by policy — by architecture.</span>
              </p>
              <div class="flex items-center gap-5 shrink-0">
                <a href="/product/security" class="font-mono text-[13px] text-[#ee6018] hover:text-white inline-flex items-center gap-1.5 transition-colors">
                  The CRF in full <span aria-hidden="true">→</span>
                </a>
                <a href="/why-corral#sovereignty" class="font-mono text-[13px] text-[#878787] hover:text-white inline-flex items-center gap-1.5 transition-colors">
                  Sovereignty deep-dive <span aria-hidden="true">→</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </GridInner>
  </GridContent>
</section>
